import * as Sentry from '@sentry/react'

import { QueryClientProvider } from '@tanstack/react-query'
import enGb from 'date-fns/locale/en-GB'
import fr from 'date-fns/locale/fr'
import NextApp from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import nextCookie from 'next-cookies'
import { useEffect } from 'react'

import '../public/assets/scroll.css'
import '../public/assets/calendarCustom.css'
import '../public/assets/modern-calendar.css'
import '../public/assets/surveyjs-modern.css'

import packageJson from '../package.json'
import './global.css'

import SentryUserSync from '@/components/App/SentryUserSync'
import AuthProvider from '@/lib/auth/AuthProvider'
import getEnvVar from '@/lib/getEnvVar'
import { initEntity } from '@/lib/hook/entityStore'
import { queryClient } from '@/lib/hook/tanstackQuery'
import { I18nProvider } from '@/locales/index'
import { entityDefault, resolveEntity } from '@/types/Entity'
import { PATIENT_DOMAINS } from '@/types/enum/constants'

// Shells chargés à la demande : un seul des deux chunks est téléchargé par
// route. Les routes `/patient/**` ne récupèrent jamais le code backoffice
// (Apollo, Companion, ProfileLoader, thème multi-entité) et inversement.
const MainAppShell = dynamic(() => import('@/components/App/MainAppShell'))
const PatientAppShell = dynamic(() => import('@/components/App/PatientAppShell'))

if (getEnvVar('APP_ENV') === 'production' || getEnvVar('APP_ENV') === 'staging') {
  Sentry.init({
    dsn: 'https://21a667c4b2ba48839dc2b5a534c0c858@o395580.ingest.sentry.io/5399179',
    // @ts-ignore
    // integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.8,
    release: 'pharmedigroup@' + packageJson.version,
    environment: getEnvVar('APP_ENV'),
    ignoreErrors: ['404'],
    replaysSessionSampleRate: 0.1,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  })

  // Le Session Replay instrumente le DOM et charge un worker lourd : on le charge
  // après l'interactivité pour ne pas bloquer le thread principal au démarrage.
  if (typeof window !== 'undefined') {
    const loadReplay = async () => {
      // lazyLoadIntegration télécharge le bundle Replay depuis le CDN Sentry.
      // Cet appel peut échouer (CDN bloqué par un ad-blocker, CSP, hors-ligne)
      // et rejette alors avec « Error when loading integration: replayIntegration ».
      // Le Session Replay étant un enrichissement best-effort, on avale l'échec
      // pour éviter une unhandledrejection remontée comme une erreur applicative.
      try {
        const replay = await Sentry.lazyLoadIntegration('replayIntegration')
        Sentry.getClient()?.addIntegration(replay())
      } catch {
        // Échec non bloquant : on continue sans Session Replay.
      }
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => loadReplay())
    } else {
      setTimeout(loadReplay, 3000)
    }
  }
}

const App = ({ Component, pageProps: { session, ...pageProps }, router }) => {
  // Détermine quelle application est servie (patient autonome vs coeur de plateforme)
  const isPatientApp = router.pathname.startsWith('/patient')

  // Seed du store entité (remplace l'ancien writeQuery Apollo LOCAL.GET_ENTITY).
  // Appelé tôt dans le render, avant les enfants : initEntity ne seed que si le
  // store est encore vide (pas de réécriture cookie), donc pas de mismatch
  // d'hydratation — le client se resynchronise ensuite via le cookie.
  initEntity(pageProps.entity)

  useEffect(() => {
    // Si on est affiché dans la webview de l'application mobile, on déclare que l'application est prete
    // @ts-ignore
    if (window.ReactNativeWebView)
      // @ts-ignore
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'appReady' }))
  }, [])

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then(function (registrations) {
          for (const registration of registrations) {
            if (registration?.active?.scriptURL?.includes('service-worker.js')) {
              registration.unregister()
            }
          }
        })
        .catch(function () {
          // console.log('Erreur lors de la désinscription du Service Worker : ', error)
        })
    }
  }, [])

  const regex = new RegExp(pageProps.locale, 'g')
  const adapterLocale = regex.test('en') ? enGb : fr

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no"
        />
        <link rel="manifest" href="/manifest.json" />
      </Head>
      <AuthProvider>
        <SentryUserSync />
        <QueryClientProvider client={queryClient}>
          <I18nProvider locale={null}>
            {isPatientApp ? (
              <PatientAppShell
                Component={Component}
                pageProps={pageProps}
                adapterLocale={adapterLocale}
              />
            ) : (
              <MainAppShell
                Component={Component}
                pageProps={pageProps}
                adapterLocale={adapterLocale}
              />
            )}
          </I18nProvider>
        </QueryClientProvider>
      </AuthProvider>
    </>
  )
}

App.getInitialProps = async appContext => {
  const appProps = await NextApp.getInitialProps(appContext)

  // Détection app patient via pathname (après rewrite) OU via le domaine d'origine.
  const rawHost =
    (appContext.ctx.req?.headers['x-forwarded-host'] as string) ??
    appContext.ctx.req?.headers.host ??
    ''

  const host = rawHost.split(',')[0].trim().replace(/:\d+$/, '')
  const isPatientApp =
    appContext.router.pathname.startsWith('/patient') || PATIENT_DOMAINS.includes(host)

  if (isPatientApp) {
    return {
      pageProps: {
        ...appProps,
        host: appContext.ctx.req?.headers.host,
      },
    }
  }

  const { entity: cookieEntity = entityDefault } = nextCookie(appContext.ctx) || {}

  // Query param ?entity=sound (ou enum direct) override le cookie au login/SSR
  const queryEntityRaw = appContext.ctx.query?.entity
  const queryEntityValue = Array.isArray(queryEntityRaw) ? queryEntityRaw[0] : queryEntityRaw
  const entityFromQuery = resolveEntity(queryEntityValue as string | undefined)
  const entity = entityFromQuery ?? cookieEntity

  // L'entité est transmise via pageProps.entity puis seedée dans le store module
  // au render (cf. initEntity).
  return {
    pageProps: {
      ...appProps,
      entity,
      host: appContext.ctx.req?.headers.host,
    },
  }
}

export default App
